import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';
import { ExternalLink, RefreshCw, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyle = {
  confirmed: 'bg-emerald-500/20 text-emerald-400',
  pending: 'bg-amber-500/20 text-amber-400',
  failed: 'bg-red-500/20 text-red-400',
};

export const ChainStatusCell = ({ tx, token, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  if (!tx.on_chain) {
    return <span className="text-xs text-slate-500" data-testid={`tx-internal-${tx.transaction_id}`}>Internal ledger</span>;
  }
  const refresh = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/pas-admin/transactions/${tx.transaction_id}/refresh-status`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Status: ${res.data.status}`);
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Status check failed');
    } finally {
      setBusy(false);
    }
  };
  const status = tx.chain_status || 'pending';
  return (
    <div className="flex items-center justify-end gap-2">
      <span className={`px-2 py-0.5 rounded text-xs ${statusStyle[status]}`} data-testid={`tx-chain-status-${tx.transaction_id}`}>{status}</span>
      {status === 'pending' && (
        <button onClick={refresh} disabled={busy} className="text-slate-400 hover:text-white" data-testid={`tx-refresh-${tx.transaction_id}`}>
          <RefreshCw className={`w-4 h-4 ${busy ? 'animate-spin' : ''}`} />
        </button>
      )}
      <a href={tx.etherscan_url} target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300" data-testid={`tx-etherscan-${tx.transaction_id}`}>
        <ExternalLink className="w-4 h-4" />
      </a>
    </div>
  );
};

export const TxResultModal = ({ result, onClose }) => {
  if (!result) return null;
  const tx = result.transaction;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="tx-result-modal">
      <div className="bg-slate-800 rounded-2xl p-6 max-w-md w-full relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white" data-testid="tx-result-close-btn">
          <X className="w-5 h-5" />
        </button>
        <h3 className="text-xl font-bold text-white mb-1">Transfer sent</h3>
        <p className="text-slate-400 text-sm mb-4" data-testid="tx-result-message">{result.message}</p>
        <p className="text-xs text-slate-500 uppercase">{tx.on_chain ? 'Sepolia/Mainnet transaction hash' : 'Internal reference'}</p>
        <code className="block text-emerald-400 text-xs break-all mb-4" data-testid="tx-result-hash">{tx.tx_hash}</code>
        {tx.on_chain ? (
          <div className="flex items-center gap-4">
            <div className="bg-white p-2 rounded">
              <QRCodeSVG value={tx.etherscan_url} size={96} data-testid="tx-result-qr" />
            </div>
            <div className="text-sm">
              <p className="text-amber-400 mb-2">Waiting for block confirmation (usually under a minute)</p>
              <a href={tx.etherscan_url} target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 flex items-center gap-1" data-testid="tx-result-etherscan-link">
                View on Etherscan <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        ) : (
          <p className="text-slate-400 text-sm">Recorded on the internal BETH ledger. Balances are already updated.</p>
        )}
      </div>
    </div>
  );
};
